import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useDatabase } from '../../context/DatabaseContext';

const { FiExternalLink, FiInfo, FiPackage } = FiIcons;

const SearchResults = ({ results, loading, query, totalResults }) => {
  const { getCategoryName } = useDatabase();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-600">Suche läuft...</span>
        </div>
      </div>
    );
  }

  if (!query && results.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <SafeIcon icon={FiPackage} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Geben Sie einen Suchbegriff ein
        </h3>
        <p className="text-gray-600">
          Suchen Sie nach Artikelnummern, Herstellern oder technischen Spezifikationen
        </p>
      </div>
    );
  }

  if (results.length === 0 && query) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <SafeIcon icon={FiInfo} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Keine Ergebnisse gefunden
        </h3>
        <p className="text-gray-600 mb-4">
          Für "{query}" wurden keine passenden Produkte gefunden.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Suchergebnisse
            </h2>
            <p className="text-sm text-gray-600">
              {results.length} Produkte gefunden
              {query && ` für "${query}"`}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500">
              <option>Relevanz</option>
              <option>Name A-Z</option>
              <option>Hersteller A-Z</option>
            </select>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {results.map((product, index) => (
          <motion.div
            key={product.id}
            className="p-6 hover:bg-red-50/30 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                    <SafeIcon icon={FiPackage} className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Link
                          to={`/product/${product.id}`}
                          className="text-lg font-semibold text-red-600 hover:text-red-700"
                        >
                          {product.manufacturer_sku}
                        </Link>
                        <p className="text-gray-600 text-sm">{product.manufacturer}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${
                          product.availability === 'In Stock' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {product.availability === 'In Stock' ? 'Auf Lager' : 'Nicht verfügbar'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 mb-3">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium border border-gray-200">
                        {getCategoryName(product.etim_class_code)}
                      </span>
                      <span className="text-xs text-gray-500 font-mono">
                        {product.etim_class_code}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {product.specifications.inner_diameter_mm && (
                        <div>
                          <span className="text-gray-500">Innen-Ø:</span>
                          <span className="ml-1 font-medium text-gray-900">
                            {product.specifications.inner_diameter_mm}mm
                          </span>
                        </div>
                      )}
                      {product.specifications.outer_diameter_mm && (
                        <div>
                          <span className="text-gray-500">Außen-Ø:</span>
                          <span className="ml-1 font-medium text-gray-900">
                            {product.specifications.outer_diameter_mm}mm
                          </span>
                        </div>
                      )}
                      {product.specifications.width_mm && (
                        <div>
                          <span className="text-gray-500">Breite:</span>
                          <span className="ml-1 font-medium text-gray-900">
                            {product.specifications.width_mm}mm
                          </span>
                        </div>
                      )}
                      {product.specifications.material && (
                        <div>
                          <span className="text-gray-500">Material:</span>
                          <span className="ml-1 font-medium text-gray-900">
                            {product.specifications.material}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="ml-4 flex flex-col space-y-2">
                <Link
                  to={`/product/${product.id}`}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm shadow-sm"
                >
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  <span>Details</span>
                </Link>
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 hover:text-black transition-colors text-sm bg-white">
                  Anfrage
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SearchResults;